import * as React from 'react';
import { IntlShape } from 'react-intl';
import moment from 'moment';
import {
  getRenderLink,
  renderDateTime,
  alphabeticalSorterFor,
  chronologicalSorterFor,
} from '../shared/TableLayout';
import TripStatus from '../../components/shared/TripStatus';
import { formatDistance, formatDuration } from '../../utils';
import isDesktop from '../../utils/sizeHelpers';

const getColumns = (intl: IntlShape): any[] => {
  const renderStatus = (status: string) => <TripStatus status={status} />;
  const renderDuration = (duration: string, trip: { status: string }) => {
    if (!duration || trip.status !== 'DONE') {
      return <span>-</span>;
    }
    return <span>{formatDuration(duration)}</span>;
  };

  const renderDistance = (distance: number, trip: { status: string }) => {
    return (
      <span>{trip.status === 'DONE' ? formatDistance(distance) : '-'}</span>
    );
  };

  const renderViolation = (
    violation: string,
    trip: { status: string; endViolations: string | any[] }
  ) => (
    <span>
      {trip.status === 'DONE'
        ? trip.endViolations.includes(violation)
          ? 'True'
          : 'False'
        : '-'}
    </span>
  );

  return [
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.tripid',
        defaultMessage: 'Trip ID',
      }),
      dataIndex: 'code',
      render: getRenderLink(),
      ellipsis: true,
      width: isDesktop() ? 120 : 100,
      fixed: 'left' as 'left',
      sorter: alphabeticalSorterFor('code'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.status',
        defaultMessage: 'Status',
      }),
      dataIndex: 'status',
      render: renderStatus,
      width: 150,
      sorter: alphabeticalSorterFor('status'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.plate',
        defaultMessage: 'Plate No.',
      }),
      dataIndex: 'plate',
      width: 150,
      sorter: alphabeticalSorterFor('plate'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.contractid',
        defaultMessage: 'Contract ID',
      }),
      dataIndex: 'merchantRef',
      width: 150,
      sorter: alphabeticalSorterFor('merchantRef'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.fleet',
        defaultMessage: 'Fleet',
      }),
      dataIndex: 'fleetName',
      ellipsis: true,
      width: 200,
      sorter: alphabeticalSorterFor('fleetName'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.user',
        defaultMessage: 'User',
      }),
      dataIndex: 'userReferralCode',
      width: 120,
      sorter: alphabeticalSorterFor('userReferralCode'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.startedat',
        defaultMessage: 'Started at',
      }),
      dataIndex: 'tripStart',
      width: 150,
      ellipsis: true,
      render: renderDateTime,
      sorter: chronologicalSorterFor('tripStart'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.finishedat',
        defaultMessage: 'Finished at',
      }),
      dataIndex: 'tripEnd',
      width: 150,
      ellipsis: true,
      render: renderDateTime,
      sorter: chronologicalSorterFor('tripEnd'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.insurance',
        defaultMessage: 'Insurance',
      }),
      dataIndex: 'insurance',
      width: 120,
      sorter: alphabeticalSorterFor('insurance'),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.duration',
        defaultMessage: 'Duration',
      }),
      width: 200,
      dataIndex: 'duration',
      ellipsis: true,
      render: renderDuration,
      sorter: (
        tripA: { duration: moment.DurationInputArg1 },
        tripB: { duration: moment.DurationInputArg1 }
      ) =>
        moment.duration(tripA.duration).asDays() -
        moment.duration(tripB.duration).asDays(),
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.mileage',
        defaultMessage: 'Mileage',
      }),
      dataIndex: 'distance',
      width: 120,
      render: renderDistance,
      sorter: (tripA: { distance: number }, tripB: { distance: number }) => {
        return tripA.distance - tripB.distance;
      },
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.violation.missingkey',
        defaultMessage: 'Violation: Missing key',
      }),
      dataIndex: 'violationMissionKey',
      render: (
        _: any,
        trip: { status: string; endViolations: string | any[] }
      ) => renderViolation('keyMissing', trip),
      width: 200,
      ellipsis: true,
      sorter: (
        tripA: { endViolations: { includes: (arg0: string) => number } },
        tripB: { endViolations: { includes: (arg0: string) => number } }
      ) => {
        return (
          tripA.endViolations.includes('keyMissing') -
          tripB.endViolations.includes('keyMissing')
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.violation.missingfuelcard',
        defaultMessage: 'Violation: Missing fuel card',
      }),
      dataIndex: 'violationMissingFuelCard',
      render: (
        _: any,
        trip: { status: string; endViolations: string | any[] }
      ) => renderViolation('fuelCardMissing', trip),
      width: 230,
      ellipsis: true,
      sorter: (
        tripA: { endViolations: { includes: (arg0: string) => number } },
        tripB: { endViolations: { includes: (arg0: string) => number } }
      ) => {
        return (
          tripA.endViolations.includes('fuelCardMissing') -
          tripB.endViolations.includes('fuelCardMissing')
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.violation.returnarea',
        defaultMessage: 'Violation: Return area',
      }),
      dataIndex: 'violationReturnArea',
      render: (
        _: any,
        trip: { status: string; endViolations: string | any[] }
      ) => renderViolation('returnedOutsideArea', trip),
      width: 200,
      ellipsis: true,
      sorter: (
        tripA: { endViolations: { includes: (arg0: string) => number } },
        tripB: { endViolations: { includes: (arg0: string) => number } }
      ) => {
        return (
          tripA.endViolations.includes('returnedOutsideArea') -
          tripB.endViolations.includes('returnedOutsideArea')
        );
      },
    },
    {
      title: intl.formatMessage({
        id: 'fleetadmin.trip.table.violation.lowfuel',
        defaultMessage: 'Violation: Low fuel',
      }),
      dataIndex: 'violationLowFuel',
      render: (
        _: any,
        trip: { status: string; endViolations: string | any[] }
      ) => renderViolation('lowFuelLevel', trip),
      width: 200,
      ellipsis: true,
      sorter: (
        tripA: { endViolations: { includes: (arg0: string) => number } },
        tripB: { endViolations: { includes: (arg0: string) => number } }
      ) => {
        return (
          tripA.endViolations.includes('lowFuelLevel') -
          tripB.endViolations.includes('lowFuelLevel')
        );
      },
    },
  ];
};

export default getColumns;
